/**
 * Form state + validação + build dos requests de contato autorizado.
 *
 * Add: personId + scopes[] (≥1) + expiresAt? — espelha AddAuthorizedContactDto.
 * Update: scopes[] (≥1 se enviado) + expiresAt? — espelha UpdateAuthorizedContactDto.
 * Data do <input type=date> (YYYY-MM-DD) converte para ISO (mesmo padrão B6).
 *
 * personId é coletado como texto (honest-defer de person-picker, igual ao B6
 * wardId/guardianId) — o contato autorizado é um TERCEIRO, não o titular da tela.
 */

import type {
  AddAuthorizedContactRequest,
  UpdateAuthorizedContactRequest,
} from '../../../lib/api-client';

export interface AuthorizedContactFormState {
  personId: string;
  scopes: string[];
  expiresAt: string;
}

export function initialAddForm(): AuthorizedContactFormState {
  return { personId: '', scopes: [], expiresAt: '' };
}

/** Form de edição pré-preenchido a partir do contato existente. expiresAt ISO → YYYY-MM-DD. */
export function editFormFrom(contact: {
  personId: string;
  scopes: string[];
  expiresAt: string | null;
}): AuthorizedContactFormState {
  return {
    personId: contact.personId,
    scopes: [...contact.scopes],
    expiresAt: contact.expiresAt !== null ? isoToDateInput(contact.expiresAt) : '',
  };
}

function isoToDateInput(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

/** YYYY-MM-DD (UTC midnight) → ISO; vazio/inválido → undefined. */
function toISODate(value: string): string | undefined {
  if (value.trim() === '') return undefined;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

export function toggleScope(scopes: string[], scope: string): string[] {
  return scopes.includes(scope) ? scopes.filter((s) => s !== scope) : [...scopes, scope];
}

/** Validação do form de adição (personId + ≥1 escopo obrigatórios). */
export function validateAddForm(form: AuthorizedContactFormState): string | null {
  if (form.personId.trim() === '') return 'Informe o ID da pessoa autorizada.';
  if (form.scopes.length === 0) return 'Selecione ao menos um escopo de autorização.';
  return null;
}

/** Validação do form de edição (≥1 escopo; personId é imutável aqui). */
export function validateEditForm(form: AuthorizedContactFormState): string | null {
  if (form.scopes.length === 0) return 'Selecione ao menos um escopo de autorização.';
  return null;
}

export function buildAddRequest(form: AuthorizedContactFormState): AddAuthorizedContactRequest {
  const req: AddAuthorizedContactRequest = {
    personId: form.personId.trim(),
    scopes: [...form.scopes],
  };
  const expiresAt = toISODate(form.expiresAt);
  if (expiresAt !== undefined) req.expiresAt = expiresAt;
  return req;
}

export function buildUpdateRequest(
  form: AuthorizedContactFormState,
): UpdateAuthorizedContactRequest {
  const req: UpdateAuthorizedContactRequest = { scopes: [...form.scopes] };
  const expiresAt = toISODate(form.expiresAt);
  if (expiresAt !== undefined) req.expiresAt = expiresAt;
  return req;
}

export const REVOKE_REASON_MIN = 5;

export function validateRevokeReason(reason: string): string | null {
  if (reason.trim().length < REVOKE_REASON_MIN) {
    return `Informe um motivo (mínimo ${REVOKE_REASON_MIN} caracteres).`;
  }
  return null;
}
